<!-- La validation applicative fournit des messages par champ et gère le focus des erreurs. -->
<form (submit)="soumettre($event)" [attr.aria-busy]="envoi()" novalidate>
  <header>
    <span class="material-symbols-outlined icone" aria-hidden="true">home_repair_service</span>
    <div>
      <h2 tabindex="-1">{{ titre() }}</h2>
      <p class="aide">Les champs marqués d’un astérisque sont obligatoires.</p>
    </div>
    <button
      type="button"
      class="fermer"
      aria-label="Fermer"
      [disabled]="envoi()"
      (click)="annuler.emit()"
    >
      <span class="material-symbols-outlined" aria-hidden="true">close</span>
    </button>
  </header>
  <div class="contenu">
    @if (!autorise()) {
      <p role="alert" class="erreur">Vous n’avez pas les droits nécessaires pour cette action.</p>
    }
    @if (ticketRequis() && erreurChargement()) {
      <p role="alert" class="erreur" [id]="idErreurChargementTickets">{{ erreurChargement() }}</p>
      <button type="button" (click)="recharger.emit()" [disabled]="chargement()">Réessayer</button>
    }

    <!-- Le chargement des tickets bloque leur sélection et l’envoi, mais permet de changer de centrale. -->
    <fieldset [disabled]="saisieBloquee()">
      @if (nature() === 'planification') {
        <label
          >Type d’intervention *
          <select
            #categorie
            data-champ="categorie"
            [formField]="formulaire.categorie"
            [attr.aria-invalid]="!!erreurChamp('categorie')"
            [attr.aria-describedby]="erreurChamp('categorie') ? idErreur('categorie') : null"
            (change)="changerCategorie(categorie.value)"
          >
            <option value="preventive">Préventive</option>
            <option value="curative">Curative</option>
          </select>
          @if (erreurChamp('categorie'); as erreur) {
            <span class="erreur" [id]="idErreur('categorie')">{{ erreur }}</span>
          }
        </label>
      }
      <!-- Le texte libre sert à chercher ; seule une option choisie fournit un identifiant valide. -->
      <label
        >Centrale *
        <input
          #installation
          #listeInstallations="matAutocompleteTrigger"
          type="text"
          data-champ="installationId"
          aria-required="true"
          placeholder="Rechercher une centrale par nom ou identifiant"
          autocomplete="off"
          [value]="rechercheInstallation()"
          [matAutocomplete]="centrales"
          [readonly]="contexteVerrouille()"
          [matAutocompleteDisabled]="contexteVerrouille()"
          [attr.aria-invalid]="!!erreurChamp('installationId')"
          [attr.aria-describedby]="
            erreurChamp('installationId') ? idErreur('installationId') : null
          "
          (input)="rechercherInstallation(installation.value)"
        />
        <mat-autocomplete
          #centrales="matAutocomplete"
          class="intervention-centrales-panel"
          [displayWith]="libelleInstallation"
          (optionSelected)="
            selectionnerOption('installation', $event.option.value, listeInstallations)
          "
        >
          @for (item of installationsFiltrees(); track item.id) {
            <mat-option [value]="'' + item.id">{{ item.nom }}</mat-option>
          } @empty {
            <mat-option disabled>Aucune centrale trouvée</mat-option>
          }
        </mat-autocomplete>
        @if (erreurChamp('installationId'); as erreur) {
          <span class="erreur" [id]="idErreur('installationId')">{{ erreur }}</span>
        }
      </label>

      @if (ticketRequis()) {
        <div class="champ-ticket">
          <label
            >Ticket *
            <input
              #ticket
              #listeTickets="matAutocompleteTrigger"
              type="text"
              data-champ="ticketId"
              aria-required="true"
              placeholder="Rechercher un ticket par numéro ou libellé"
              autocomplete="off"
              [value]="rechercheTicket()"
              [matAutocomplete]="choixTickets"
              [readonly]="contexteVerrouille()"
              [disabled]="ticketsIndisponibles()"
              [matAutocompleteDisabled]="contexteVerrouille() || ticketsIndisponibles()"
              [attr.aria-invalid]="!!erreurChamp('ticketId')"
              [attr.aria-describedby]="descriptionTicket()"
              (input)="rechercherTicket(ticket.value)"
            />
            <mat-autocomplete
              #choixTickets="matAutocomplete"
              class="intervention-centrales-panel"
              [displayWith]="libelleTicket"
              (optionSelected)="selectionnerOption('ticket', $event.option.value, listeTickets)"
            >
              <!-- Tous les états restent accessibles, y compris les tickets résolus. -->
              @for (groupe of groupesTickets(); track groupe.etat) {
                <mat-optgroup [label]="groupe.libelle">
                  @for (item of groupe.tickets; track item.id) {
                    <mat-option [value]="'' + item.id"
                      >{{ item.numero }} — {{ item.libelle }}</mat-option
                    >
                  }
                </mat-optgroup>
              } @empty {
                <mat-option disabled>Aucun ticket trouvé</mat-option>
              }
            </mat-autocomplete>
            @if (erreurChamp('ticketId'); as erreur) {
              <span class="erreur" [id]="idErreur('ticketId')">{{ erreur }}</span>
            }
          </label>
          <!-- Garder ce statut sous le champ Ticket, hors du label et relié par aria-describedby. -->
          @if (chargement()) {
            <p class="aide chargement-tickets" role="status" [id]="idChargementTickets">
              Chargement des tickets de la centrale…
            </p>
          }
        </div>
      }

      <!-- Présentation temporaire : ces intervenants ne sont ni éditables ni envoyés à l’API. -->
      @if (afficherIntervenants()) {
        <section
          class="intervenants-ticket"
          aria-label="Intervenants du ticket — données de démonstration"
        >
          <div class="intervenants-entete">
            <span class="intervenants-titre">Intervenants du ticket</span>
            <span class="intervenants-demo">Données de démonstration</span>
          </div>
          <ul>
            @for (intervenant of intervenantsDemonstration; track intervenant.libelle) {
              <li>
                <span class="material-symbols-outlined" aria-hidden="true">{{
                  intervenant.icone
                }}</span>
                <span>{{ intervenant.libelle }}</span>
              </li>
            }
          </ul>
        </section>
      }

      @if (nature() === 'planification') {
        <label
          >Date de l’intervention *
          <input
            data-champ="date"
            type="date"
            [min]="dateMinPlanification()"
            [formField]="formulaire.date"
            [attr.aria-invalid]="!!erreurChamp('date')"
            [attr.aria-describedby]="erreurChamp('date') ? idErreur('date') : null"
          />
          @if (erreurChamp('date'); as erreur) {
            <span class="erreur" [id]="idErreur('date')">{{ erreur }}</span>
          }
        </label>
        <p class="aide">
          Les heures de début et de fin sont facultatives et exprimées en heure de Paris.
        </p>
        <div class="horaires">
          <label
            >Début
            <input
              data-champ="debut"
              type="time"
              [formField]="formulaire.debut"
              [attr.aria-invalid]="!!erreurChamp('debut')"
              [attr.aria-describedby]="erreurChamp('debut') ? idErreur('debut') : null"
            />
            @if (erreurChamp('debut'); as erreur) {
              <span class="erreur" [id]="idErreur('debut')">{{ erreur }}</span>
            }
          </label>
          <label
            >Fin
            <input
              data-champ="fin"
              type="time"
              [formField]="formulaire.fin"
              [attr.aria-invalid]="!!erreurChamp('fin')"
              [attr.aria-describedby]="erreurChamp('fin') ? idErreur('fin') : null"
            />
            @if (erreurChamp('fin'); as erreur) {
              <span class="erreur" [id]="idErreur('fin')">{{ erreur }}</span>
            }
          </label>
        </div>
      }

      <label
        >Description de l’intervention *
        <textarea
          data-champ="description"
          rows="6"
          [formField]="formulaire.description"
          [attr.aria-invalid]="!!erreurChamp('description')"
          [attr.aria-describedby]="erreurChamp('description') ? idErreur('description') : null"
        ></textarea>
        @if (erreurChamp('description'); as erreur) {
          <span class="erreur" [id]="idErreur('description')">{{ erreur }}</span>
        }
      </label>

      <!-- Les photos déjà enregistrées sont consultables ; seuls les nouveaux fichiers sont retirables. -->
      @if (nature() === 'bon') {
        @if (photosExistantes().length) {
          <p class="aide">Photos déjà enregistrées (conservées lors de la modification) :</p>
          <app-intervention-photos [photos]="photosExistantes()" />
        }
        <label
          >Ajouter des photos
          <input
            type="file"
            data-champ="photos"
            [accept]="typesPhotosAcceptes"
            multiple
            [attr.aria-invalid]="!!erreurPhotos() || !!erreurPhotosServeur()"
            [attr.aria-describedby]="
              erreurPhotos() || erreurPhotosServeur() ? idErreur('photos') : null
            "
            (change)="ajouterPhotos($event)"
          />
        </label>
        @if (erreurPhotos() || erreurPhotosServeur()) {
          <p role="alert" class="erreur" [id]="idErreur('photos')">
            {{ erreurPhotos() }} {{ erreurPhotosServeur() }}
          </p>
        }
        <p class="aide">JPEG, PNG ou WebP · 10 Mo par photo · 10 photos maximum.</p>
        <ul aria-label="Nouvelles photos">
          @for (photo of photos(); track $index) {
            <li>
              <span>{{ photo.name }}</span>
              <button
                type="button"
                [attr.aria-label]="'Retirer la photo ' + photo.name"
                (click)="retirerPhoto($index)"
              >
                Retirer
              </button>
            </li>
          }
        </ul>
        <label class="case">
          <input
            type="checkbox"
            data-champ="problemeResolu"
            [formField]="formulaire.problemeResolu"
            [attr.aria-invalid]="!!erreurChamp('problemeResolu')"
            [attr.aria-describedby]="
              erreurChamp('problemeResolu') ? idErreur('problemeResolu') : null
            "
          />
          Problème résolu
          <span class="aide">Le ticket sera fermé lors de l’enregistrement du bon.</span>
        </label>
        @if (erreurChamp('problemeResolu'); as erreur) {
          <p class="erreur" [id]="idErreur('problemeResolu')">{{ erreur }}</p>
        }
      }
    </fieldset>

    <!-- Toujours monté, même vide : le composant peut y placer le focus dès le refus serveur. -->
    <div #resumeErreurs tabindex="-1" role="alert" class="erreur">
      @if (tentative() && erreurs().length) {
        <p>Vérifiez les champs indiqués avant d’enregistrer.</p>
      }
      @if (erreurEnvoi()) {
        <p>{{ erreurEnvoi() }}</p>
        @if (erreursServeurAffichees().length || detailsErreurAffiches().length) {
          <ul class="details-erreurs" aria-label="Détails des erreurs d’enregistrement">
            @for (erreur of erreursServeurAffichees(); track erreur.champ) {
              <li>
                <strong>{{ erreur.libelle }} :</strong> {{ erreur.message }}
              </li>
            }
            @for (detail of detailsErreurAffiches(); track detail) {
              <li>{{ detail }}</li>
            }
          </ul>
        }
      }
    </div>
    @if (termine()) {
      <p role="status">L’intervention a été enregistrée.</p>
    }
    @if (!sauvegardeDisponible()) {
      <p class="information" role="status">
        L’enregistrement n’est pas encore disponible. Fermer cette fenêtre efface la saisie.
      </p>
    }
  </div>
  <footer>
    <button type="button" [disabled]="envoi()" (click)="annuler.emit()">
      {{ termine() ? 'Fermer' : 'Annuler' }}
    </button>
    <button type="submit" class="principal" [disabled]="indisponible() || !sauvegardeDisponible()">
      {{ envoi() ? 'Enregistrement…' : edition() ? 'Enregistrer' : 'Créer' }}
    </button>
  </footer>
</form>
